/** @flow */

import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { route } from "@routes/helper";
import { Row, Col, Pagination, GSGrid } from "@components";
import { getEmployeesSort } from "@modules/employee/selectors";
import actionCreators from "@modules/employee/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import { Link } from "react-router-dom";
import { formatDate } from "@transformers/date";
import { toTableSortV2 } from "@transformers/sort";
import * as Paths from "@routes/constants";
import type { PaginationExtendedType } from "@types";
import type { State } from "@store/reducers";
import type { EmployeeType } from "@modules/employee/types";

import { SecurityNotice } from "@modules/shared/components";

import moment from "moment";

import "./index.scss";

type OwnProps = $Exact<{
  employees: Array<EmployeeType>,
  clientId: number,
  pagination: PaginationExtendedType,
  selectEmployeeLink: $Keys<typeof Paths>,
  onSortChange: Function,
  onPageChange: Function,
}>;

type PropsFromState = $Exact<{
  sort: Array<Object>,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof actionCreators.GetEmployees,
  SaveEmployeeRecordAccess: typeof sharedActionCreators.SaveEmployeeRecordAccess,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export function EmployeeList(props: Props) {
  const notices = [
    "EMPLOYEE_CONFIDENTIAL_INTOFRMATION",
    "EMPLOYEE_PERMISSION_INFORMATION",
    "EMPLOYEE_SURROUNDINGS_WARNING",
    "EMPLOYEE_ACCESS_INFORMATION",
  ];
  const columns = [
    {
      key: "employeeRefPrimary",
      name: props.t("EMPLOYEE_REF"),
      width: 200,
      align: "left",
      renderCell: ({ row }: { row: EmployeeType }) =>
        GSGrid.render.ellipsis(row.employeeRefPrimary),
    },
    {
      key: "firstName",
      width: 150,
      name: props.t("FIRST_NAME"),
      renderCell: ({ row }: { row: EmployeeType }) => GSGrid.render.ellipsis(row.firstName),
      align: "left",
    },
    {
      key: "lastName",
      width: 150,
      name: props.t("SURNAME"),
      renderCell: ({ row }: { row: EmployeeType }) => GSGrid.render.ellipsis(row.lastName),
      sortable: true,
      align: "left",
    },
    {
      key: "dateOfBirth",
      name: props.t("DATE_OF_BIRTH"),
      renderCell: ({ row }: { row: EmployeeType }) => formatDate(row.dateOfBirth),
      sortable: true,
    },
    {
      key: "department",
      name: props.t("DEPARTMENT"),
      align: "left",
      renderCell: ({ row }: { row: EmployeeType }) =>
        GSGrid.render.ellipsis(
          row.employmentSummary.organisationalUnits.length
            ? row.employmentSummary.organisationalUnits.join(", ")
            : props.t("N/A"),
        ),
    },
    {
      key: "isActive",
      name: props.t("STATUS"),
      minWidth: 64,
      renderCell: ({ row }: { row: EmployeeType }) =>
        GSGrid.render.statusPill(row.isActive ? props.t("ACTIVE") : props.t("INACTIVE")),
      sortable: true,
    },

    {
      key: "isAbsent",
      name: props.t("CURRENTLY_ABSENT"),
      align: "center",
      sortable: true,
      renderCell: ({ row }: { row: EmployeeType }) => {
        if (row.currentAbsence) {
          let text = "Yes";

          if (row.currentAbsence.status === "OVERDUE") {
            text += ` (${props.t("OVERDUE")})`;
          } else if (row.currentAbsence.status === "OPEN" && row.currentAbsence.rtwAt) {
            text += ` (RTW: ${moment(row.currentAbsence.rtwAt).format("DD/MM/YYYY")})`;
          }

          return <div className="text-negative font-weight-bold">{text}</div>;
        }

        return <strong>{props.t("NO")}</strong>;
      },
    },
    {
      key: "id",
      align: "center",
      name: props.t("USER_ID"),
      sortable: true,
      minWidth: 64,
    },
    {
      name: props.t("SELECT"),
      key: "actions",
      width: 80,
      frozen: "right",
      renderCell: ({ row }: { row: EmployeeType }) => (
        <Link
          onClick={saveEmployeeAccess(row)}
          to={route(props.selectEmployeeLink, {
            employeeId: row.id,
            clientId: props.clientId,
          })}
          aria-label={props.t("SELECT_EMPLOYEE")}
          className="d-flex"
        >
          <span className="material-icons font-size-35 pointer">play_circle</span>
        </Link>
      ),
    },
  ];

  function onSort(sort: Object) {
    props.onSortChange(sort);
  }

  function fetchEmployees(page: number) {
    props.onPageChange(page);
  }

  function saveEmployeeAccess(employee: EmployeeType) {
    return function() {
      props.SaveEmployeeRecordAccess({
        clientId: props.clientId,
        employeeId: employee.id,
      });
    };
  }

  if (!props.pagination?.pageNumber) {
    return <SecurityNotice notices={notices} wrapperClassName="pt-4" />;
  }

  return (
    <div className="employee-list">
      <GSGrid
        className="mt-3"
        rows={props.employees}
        columns={columns}
        onSortChange={onSort}
        sortColumns={toTableSortV2(props.sort)}
        noResultsText={props.t("NO_EMPLOYEES_MESSAGE")}
      />
      {props.pagination && (
        <Pagination
          currentPage={props.pagination.pageNumber}
          totalPages={props.pagination.totalPages}
          onChange={fetchEmployees}
        />
      )}
    </div>
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  sort: getEmployeesSort(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: actionCreators.GetEmployees,
  SaveEmployeeRecordAccess: sharedActionCreators.SaveEmployeeRecordAccess,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(EmployeeList));
